import dayjs from "dayjs";
import { useMemo, type FC } from "react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import {
  WEEKDAY_LABELS,
  eventsOnDay,
  monthGrid,
  type CalendarEvent,
  type DayCell
} from "../calendarModel";
import { EventPill } from "./EventChip";

export const MONTH_MAX_VISIBLE = 3;

type Props = {
  anchor: Date;
  events: CalendarEvent[];
  highlightedId: string | null;
  onOpenEvent: (event: CalendarEvent) => void;
  /**
   * Click on the empty part of a day cell; `allDay` is always true here.
   * Omit for a read-only grid.
   */
  onCreateAt?: (start: Date, allDay: boolean) => void;
  now?: Date;
};

const DayNumber: FC<{ cell: DayCell }> = ({ cell }) => (
  <span
    className={cn(
      "inline-flex size-6 items-center justify-center rounded-full text-xs font-medium tabular-nums",
      cell.isToday && "bg-primary text-primary-foreground",
      !cell.isToday && cell.isOutside && "text-fg-4"
    )}
  >
    {cell.date.getDate()}
  </span>
);

const MoreButton: FC<{
  cell: DayCell;
  hidden: CalendarEvent[];
  all: CalendarEvent[];
  highlightedId: string | null;
  onOpenEvent: (event: CalendarEvent) => void;
  now?: Date;
}> = ({ cell, hidden, all, highlightedId, onOpenEvent, now }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type="button"
        onClick={(e) => e.stopPropagation()}
        aria-label={`${hidden.length} more on ${dayjs(cell.date).format(
          "MMM D"
        )}`}
        className="h-5 w-full rounded-sm px-1.5 text-left text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        + {hidden.length} more
      </button>
    </PopoverTrigger>
    <PopoverContent
      align="start"
      className="w-64 p-2"
      onClick={(e) => e.stopPropagation()}
    >
      <p className="mb-2 px-1 text-xs font-semibold">
        {dayjs(cell.date).format("dddd, MMMM D")}
      </p>
      <div className="flex flex-col gap-1">
        {all.map((event) => (
          <EventPill
            key={event.id}
            event={event}
            highlighted={highlightedId === `reminder-${event.id}`}
            onOpen={onOpenEvent}
            now={now}
          />
        ))}
      </div>
    </PopoverContent>
  </Popover>
);

/** Six-week month grid with up to three pills per day and a "+N more" popover. */
export const MonthView: FC<Props> = ({
  anchor,
  events,
  highlightedId,
  onOpenEvent,
  onCreateAt,
  now
}) => {
  const cells = useMemo(() => monthGrid(anchor, now), [anchor, now]);

  return (
    <div
      className="flex h-full min-h-0 flex-col"
      role="grid"
      aria-label="Month"
    >
      <div className="grid shrink-0 grid-cols-7 border-b border-border">
        {WEEKDAY_LABELS.map((label) => (
          <div
            key={label}
            role="columnheader"
            className="px-2 py-1.5 text-center text-xs font-medium text-muted-foreground"
          >
            {label}
          </div>
        ))}
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-7 grid-rows-6">
        {cells.map((cell, index) => {
          const dayEvents = eventsOnDay(events, cell.date);
          const visible = dayEvents.slice(0, MONTH_MAX_VISIBLE);
          const hidden = dayEvents.slice(MONTH_MAX_VISIBLE);
          return (
            <div
              key={cell.key}
              role="gridcell"
              aria-label={dayjs(cell.date).format("dddd, MMMM D, YYYY")}
              data-day={cell.key}
              tabIndex={-1}
              onClick={() => onCreateAt?.(cell.date, true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.target === e.currentTarget) {
                  onCreateAt?.(cell.date, true);
                }
              }}
              className={cn(
                "flex min-h-24 flex-col gap-1 border-b border-border p-1.5 transition-colors",
                onCreateAt && "cursor-pointer hover:bg-fill-1",
                index % 7 !== 6 && "border-r",
                index >= 35 && "border-b-0",
                cell.isOutside && "bg-fill-1"
              )}
            >
              <div className="flex items-start justify-between">
                <DayNumber cell={cell} />
              </div>
              <div className="flex min-h-0 flex-col gap-0.5">
                {visible.map((event) => (
                  <EventPill
                    key={event.id}
                    event={event}
                    highlighted={highlightedId === `reminder-${event.id}`}
                    onOpen={onOpenEvent}
                    now={now}
                  />
                ))}
                {hidden.length > 0 ? (
                  <MoreButton
                    cell={cell}
                    hidden={hidden}
                    all={dayEvents}
                    highlightedId={highlightedId}
                    onOpenEvent={onOpenEvent}
                    now={now}
                  />
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
